import React, { useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";

// Internal — input body for showInputAlert. Lives in this file so a single
// import keeps the call-site clean and the styling shared with the other
// alert variants.
const InputAlertBody = ({
  title,
  infoTxt,
  placeholder,
  minLength,
  submitTxt,
  cancelTxt,
  onSubmit,
  onClose,
}) => {
  const [val, setVal] = useState("");
  const [err, setErr] = useState("");

  const submit = () => {
    const trimmed = val.trim();
    if (trimmed.length < (minLength || 1)) {
      setErr(`Minimum ${minLength || 1} characters required`);
      return;
    }
    onSubmit(trimmed);
    onClose();
  };

  return (
    <div className="react-custom-confirm-alert outer-alert">
      <div className="inner-alert">
        {title && <h1>{title}</h1>}
        {infoTxt && <p>{infoTxt}</p>}

        <input
          type="text"
          autoFocus
          value={val}
          onChange={(e) => {
            setVal(e.target.value);
            if (err) setErr("");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
          }}
          placeholder={placeholder || ""}
          style={{
            width: "100%",
            padding: "10px 12px",
            margin: "8px 0 4px",
            border: err ? "1px solid #D90000" : "1px solid #ccc",
            borderRadius: "6px",
            fontSize: "14px",
            outline: "none",
          }}
        />
        {err && (
          <p style={{ color: "#D90000", fontSize: "12px", margin: "4px 0 0" }}>
            {err}
          </p>
        )}

        <div className="alert-buttons" style={{ marginTop: "16px" }}>
          <button
            type="button"
            className="alert-btn no-btn"
            onClick={onClose}
          >
            <span className="icon no-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                className="bi bi-x-lg"
                viewBox="0 0 16 16"
              >
                <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z" />
              </svg>
            </span>
            {cancelTxt || "Cancel"}
          </button>
          <button
            type="button"
            className="alert-btn yes-btn"
            onClick={submit}
          >
            <span className="icon yes-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                className="bi bi-check2"
                viewBox="0 0 16 16"
              >
                <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0" />
              </svg>
            </span>
            {submitTxt || "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Styled input prompt — replaces window.prompt() which renders an ugly
// browser-default dialog. Calls `onSubmit(trimmedValue)` only when the
// user submits a value that passes the minLength check.
export const showInputAlert = ({
  title,
  infoTxt,
  placeholder,
  minLength,
  submitTxt,
  cancelTxt,
  onSubmit,
}) => {
  confirmAlert({
    customUI: ({ onClose }) => (
      <InputAlertBody
        title={title}
        infoTxt={infoTxt}
        placeholder={placeholder}
        minLength={minLength}
        submitTxt={submitTxt}
        cancelTxt={cancelTxt}
        onSubmit={onSubmit}
        onClose={onClose}
      />
    ),
  });
};

export const showConfirmAlert = (
  handleConfirm,
  infoTxt,
  noTxt,
  yesTxt,
  onlyWarning,
  handleCancel
) => {
  confirmAlert({
    customUI: ({ onClose }) => (
      <div className="react-custom-confirm-alert outer-alert">
        <div className="inner-alert">
          {!onlyWarning && <h1>Are you sure?</h1>}

          <p>{infoTxt}</p>

          <div className="alert-buttons">
            {!onlyWarning && (
              <button
                type="button"
                className="alert-btn no-btn"
                onClick={() => {
                  if (handleCancel) handleCancel();
                  onClose();
                }}
              >
                <span className="icon no-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    className="bi bi-x-lg"
                    viewBox="0 0 16 16"
                  >
                    <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z" />
                  </svg>
                </span>
                {noTxt ? noTxt : "No"}
              </button>
            )}
            <button
              type="button"
              className="alert-btn yes-btn"
              onClick={() => {
                handleConfirm();
                onClose();
              }}
            >
              <span className="icon yes-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  className="bi bi-check2"
                  viewBox="0 0 16 16"
                >
                  <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0" />
                </svg>
              </span>
              {yesTxt ? yesTxt : "Yes"}
            </button>
          </div>
        </div>
      </div>
    ),
  });
};
